import { NotificationManager } from '../../utils/notifications.js';
import { MenuItemModal } from '../MenuItemModal.js';
import { AnimationManager } from '../../services/AnimationManager.js';
import { setupResponsiveGrid } from '../../utils/gridColumns.js';

export class HamburgerMenuLayout {
  constructor(config, menuData, cartCount = 0, i18n = null) {
    this.config = config;
    this.menuData = menuData;
    this.cartCount = cartCount;
    const defaultCategory = this.menuData.categories?.[0]?.id || 'antipasti';
    this.isMenuOpen = false;
    this.i18n = i18n;
    this.menuItemModal = new MenuItemModal(this.i18n);
    this.animationManager = new AnimationManager();

    this.columnsPerRow = this.config?.kioskDisplaySettings?.layoutPreferences?.columnsPerRow || 3;

    this.featuredItems = this.getFeaturedItems();
    if (!this.menuData.items) {
      this.menuData.items = {};
    }
    if (this.featuredItems.length > 0) {
      this.menuData.items.featured = this.featuredItems;
    }

    this.categoriesList = this.buildCategories();
    this.currentCategory = this.categoriesList[0]?.id || defaultCategory;
  }

  render() {
    return `
      <div class="main-container hamburger-layout" style="display: none;">
        <div class="hamburger-header">
          <button class="hamburger-toggle" id="hamburger-toggle">
            <i class="fas fa-bars"></i>
          </button>
          <div class="breadcrumb">
            <span class="current-category">${this.getCurrentCategoryName()}</span>
          </div>
          <div class="logo-container">
            ${this.config.branding?.logo ?
              `<img src="${this.config.branding.logo}" alt="Logo" class="logo-small">` :
              ''}
          </div>
          <div class="header-actions">
            <button class="search-btn" id="hamburger-search-btn">
              <i class="fas fa-search"></i>
            </button>
            <button class="language-btn" id="language-btn">
              <i class="fas fa-globe"></i>
            </button>
            <button class="staff-call-btn" id="staff-call-btn">
              <i class="fas fa-bell"></i>
            </button>
            <div class="cart-btn" id="hamburger-cart-btn">
              <i class="fas fa-receipt"></i>
              <span class="cart-count">${this.cartCount}</span>
            </div>
          </div>
        </div>

        <nav class="hamburger-menu" id="hamburger-menu">
          <div class="menu-overlay" id="menu-overlay"></div>
          <div class="menu-drawer">
            <div class="menu-drawer-header">
              <h2>Menu Categories</h2>
              <button class="close-menu" id="close-menu">
                <i class="fas fa-times"></i>
              </button>
            </div>
            <ul class="menu-categories">
              ${this.categoriesList.map(category => `
                <li>
                  <button class="hamburger-category ${category.id === this.currentCategory ? 'active' : ''} ${category.isFeaturedVirtual ? 'featured-category' : ''}" data-category="${category.id}">
                    <div class="category-icon">
                      <i class="fas ${category.icon || 'fa-utensils'}"></i>
                    </div>
                    <span class="category-name">${category.name}</span>
                    <i class="fas fa-chevron-right category-arrow"></i>
                  </button>
                </li>
              `).join('')}
            </ul>
          </div>
        </nav>

        <main class="hamburger-content">
          ${this.renderCategoryContent()}
        </main>
      </div>
    `;
  }

  renderCategoryContent() {
    const categories = this.categoriesList;
    return categories.map(category => `
      <div class="category-content ${category.id === this.currentCategory ? 'active' : ''}" data-category="${category.id}">

        <div class="menu-items-grid" data-category-grid="${category.id}">
          ${(this.menuData.items[category.id] || []).map(item => {
            const images = item.images || (item.image ? [item.image] : []);
            const hasMultipleImages = images.length > 1;
            const cardClasses = ['menu-item-card'];
            if (this.isFeatured(item)) {
              cardClasses.push('is-featured');
            }

            return `
            <div class="${cardClasses.join(' ')}" data-item="${item.id}">
              <div class="item-image-container">
                ${images.length > 0 ?
                  `<img src="${images[0]}" alt="${item.name}" class="item-image">` :
                  `<div class="item-image-placeholder">
                    <i class="fas fa-utensils"></i>
                  </div>`}

            ${this.renderBadge(item)}
                ${item.dietary && item.dietary.length > 0 ? `
                  <div class="dietary-icons">
                    ${item.dietary.map(diet => `
                      <div class="dietary-icon ${diet}">
                        <i class="${this.getDietaryIcon(diet)}"></i>
                      </div>
                    `).join('')}
                  </div>
                ` : ''}
              </div>
              <div class="item-content">
                <h3 class="item-name">${item.name}</h3>
                <p class="item-description">${item.description}</p>
                <div class="item-footer">
                  <div class="item-price">${item.currency || this.config.settings?.currency || 'PLN'} ${typeof item.price === 'object' ? item.price.amount : (item.price || 0)}</div>
                  <button class="add-to-cart" data-item="${item.id}">
                    <i class="fas fa-plus"></i>
                  </button>
                </div>
              </div>
            </div>
          `;
          }).join('')}
        </div>
      </div>
    `).join('');
  }

  getDietaryIcon(dietary) {
    const icons = {
      vegetarian: 'fas fa-leaf',
      vegan: 'fas fa-seedling',
      'gluten-free': 'fas fa-wheat',
      spicy: 'fas fa-pepper-hot'
    };
    return icons[dietary] || 'fas fa-circle';
  }

  getFeaturedLabel() {
    return this.t('menu.featured', 'Featured');
  }

  buildCategories() {
    const base = Array.isArray(this.menuData.categories) ? [...this.menuData.categories] : [];
    if (this.featuredItems.length > 0) {
      const label = this.getFeaturedLabel();
      base.unshift({
        id: 'featured',
        name: label,
        icon: 'fa-star',
        description: label,
        isFeaturedVirtual: true
      });
    }
    return base;
  }

  getFeaturedItems() {
    const collected = new Map();

    if (Array.isArray(this.menuData?.specials)) {
      this.menuData.specials.forEach(item => {
        if (item && item.id) {
          collected.set(item.id, item);
        }
      });
    }

    if (this.menuData?.items) {
      Object.values(this.menuData.items).forEach(list => {
        (list || []).forEach(item => {
          if (item && item.id && this.isFeatured(item)) {
            collected.set(item.id, item);
          }
        });
      });
    }

    return Array.from(collected.values()).sort((a, b) => {
      const orderA = a.displayOrder ?? 0;
      const orderB = b.displayOrder ?? 0;
      if (orderA === orderB) {
        return a.name.localeCompare(b.name);
      }
      return orderA - orderB;
    });
  }

  getCurrentCategoryName() {
    const currentCategory = this.categoriesList.find(cat => cat.id === this.currentCategory);
    return currentCategory?.name || this.getFeaturedLabel();
  }

  getCategoryDescription(category) {
    if (category?.description && !category.description.startsWith('Category:')) {
      return category.description;
    }

    return '';
  }

  bindEvents() {
    const menuGrids = document.querySelectorAll('.hamburger-layout .menu-items-grid[data-category-grid]');
    menuGrids.forEach(grid => {
      setupResponsiveGrid(grid, this.columnsPerRow);
    });

    setTimeout(() => {
      const hamburgerToggle = document.getElementById('hamburger-toggle');
      if (hamburgerToggle) {
        const newToggle = hamburgerToggle.cloneNode(true);
        hamburgerToggle.parentNode.replaceChild(newToggle, hamburgerToggle);

        newToggle.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.toggleMenu();
        });
      }
    }, 200);

    const closeMenu = document.getElementById('close-menu');
    if (closeMenu) {
      closeMenu.addEventListener('click', () => {
        this.closeMenu();
      });
    }

    const menuOverlay = document.getElementById('menu-overlay');
    if (menuOverlay) {
      menuOverlay.addEventListener('click', () => {
        this.closeMenu();
      });
    }

    const categoryBtns = document.querySelectorAll('.hamburger-layout .hamburger-category');
    categoryBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const categoryId = btn.getAttribute('data-category');
        this.showCategory(categoryId);
        this.closeMenu();
      });
    });

    const searchBtn = document.getElementById('hamburger-search-btn');
    const cartBtn = document.getElementById('hamburger-cart-btn');

    if (searchBtn) {
      searchBtn.addEventListener('click', () => {
        NotificationManager.info('Search functionality coming soon!');
      });
    }

    if (cartBtn) {
      cartBtn.addEventListener('click', () => {
        const event = new CustomEvent('showOrderConfirmation');
        document.dispatchEvent(event);
      });
    }

    const languageBtn = document.getElementById('language-btn');
    if (languageBtn) {
      languageBtn.addEventListener('click', () => {
        const event = new CustomEvent('showLanguageSelector');
        document.dispatchEvent(event);
      });
    }

    const staffCallBtn = document.getElementById('staff-call-btn');
    if (staffCallBtn) {
      staffCallBtn.addEventListener('click', () => {
        const event = new CustomEvent('showStaffCallModal');
        document.dispatchEvent(event);
      });
    }

    const menuItemCards = document.querySelectorAll('.hamburger-layout .menu-item-card');
    menuItemCards.forEach(card => {
      card.addEventListener('click', (e) => {
        if (e.target.closest('.add-to-cart') || e.target.closest('.image-nav-btn')) {
          return;
        }

        const itemId = card.getAttribute('data-item');
        const item = this.findItemById(itemId);
        if (item) {
          const categoryItems = this.menuData.items[this.currentCategory] || [];
          const availableCategoryItems = categoryItems.filter(item => item.isAvailable !== false);
          this.menuItemModal.show(item, availableCategoryItems);
        }
      });
    });

    const addToCartBtns = document.querySelectorAll('.hamburger-layout .add-to-cart');
    addToCartBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const itemId = btn.getAttribute('data-item');
        this.handleAddToCart(itemId, btn);
      });
    });

    this.bindImageNavigation();

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isMenuOpen) {
        this.closeMenu();
      }
    });
  }

  toggleMenu() {
    if (this.isMenuOpen) {
      this.closeMenu();
    } else {
      this.openMenu();
    }
  }

  openMenu() {
    this.isMenuOpen = true;
    const menu = document.getElementById('hamburger-menu');
    const toggle = document.getElementById('hamburger-toggle');

    if (menu) {
      menu.classList.add('open');
      menu.style.visibility = 'visible';
      menu.style.opacity = '1';
      menu.style.pointerEvents = 'auto';
    }

    if (toggle) {
      const icon = toggle.querySelector('i');
      if (icon) {
        icon.className = 'fas fa-times';
      }
    }
  }

  closeMenu() {
    this.isMenuOpen = false;
    const menu = document.getElementById('hamburger-menu');
    const toggle = document.getElementById('hamburger-toggle');

    if (menu) {
      menu.classList.remove('open');
      menu.style.visibility = 'hidden';
      menu.style.opacity = '0';
      menu.style.pointerEvents = 'none';
    }

    if (toggle) {
      const icon = toggle.querySelector('i');
      if (icon) {
        icon.className = 'fas fa-bars';
      }
    }
  }

  async showCategory(categoryId) {
    if (this.currentCategory === categoryId) return;

    const oldCategory = this.currentCategory;
    this.currentCategory = categoryId;

    const breadcrumbEl = document.querySelector('.hamburger-layout .current-category');
    if (breadcrumbEl) {
      this.animationManager.animateSectionTitle(breadcrumbEl, this.getCurrentCategoryName());
    }

    const categoryBtns = document.querySelectorAll('.hamburger-layout .hamburger-category');
    categoryBtns.forEach(btn => {
      if (btn.getAttribute('data-category') === categoryId) {
        btn.classList.add('active');
        this.animationManager.animateCategorySelection(btn);
      } else {
        btn.classList.remove('active');
      }
    });

    const oldContent = document.querySelector(`.hamburger-layout .category-content[data-category="${oldCategory}"]`);
    const newContent = document.querySelector(`.hamburger-layout .category-content[data-category="${categoryId}"]`);

    if (oldContent && newContent) {
      const newMenuGrid = newContent.querySelector('.menu-items-grid');

      if (newMenuGrid) {
        newMenuGrid.style.opacity = '0';
        newMenuGrid.style.transition = 'opacity 0.3s ease-in-out';
      }

      await this.animationManager.animateCategoryTransition(oldContent, newContent, 'slide');

      const contents = document.querySelectorAll('.hamburger-layout .category-content');
      contents.forEach(content => {
        if (content.getAttribute('data-category') === categoryId) {
          content.classList.add('active');
        } else {
          content.classList.remove('active');
        }
      });

      if (newMenuGrid) {
        newMenuGrid.innerHTML = this.renderCategoryMenuItems(categoryId);
        setupResponsiveGrid(newMenuGrid, this.columnsPerRow);

        setTimeout(() => {
          newMenuGrid.style.opacity = '1';
          this.animationManager.animateMenuItems(newMenuGrid, 30);

          this.rebindCategoryEvents(newContent);
        }, 50);
      }

      const contentContainer = document.querySelector('.hamburger-layout .hamburger-content');
      if (contentContainer) {
        this.animationManager.smoothScrollToTop(contentContainer);
      }
    }
  }

  renderCategoryMenuItems(categoryId) {
    const items = this.menuData.items[categoryId] || [];
    const availableItems = items.filter(item => item.isAvailable !== false);
    return availableItems.map(item => {
      const images = item.images || (item.image ? [item.image] : []);
      const cardClasses = ['menu-item-card'];
      if (this.isFeatured(item)) {
        cardClasses.push('is-featured');
      }

      return `
      <div class="${cardClasses.join(' ')}" data-item="${item.id}">
        <div class="item-image-container">
          ${images.length > 0 ?
            `<img src="${images[0]}" alt="${item.name}" class="item-image">` :
            `<div class="item-image-placeholder">
              <i class="fas fa-utensils"></i>
            </div>`}

          ${this.renderBadge(item)}
          ${item.dietary && item.dietary.length > 0 ? `
            <div class="dietary-icons">
              ${item.dietary.map(diet => `
                <div class="dietary-icon ${diet}">
                  <i class="${this.getDietaryIcon(diet)}"></i>
                </div>
              `).join('')}
            </div>
          ` : ''}
        </div>
        <div class="item-content">
          <h3 class="item-name">${item.name}</h3>
          <p class="item-description">${item.description}</p>
          <div class="item-footer">
            <div class="item-price">${item.currency || this.config.settings?.currency || 'PLN'} ${typeof item.price === 'object' ? item.price.amount : (item.price || 0)}</div>
            <button class="add-to-cart" data-item="${item.id}">
              <i class="fas fa-plus"></i>
            </button>
          </div>
        </div>
      </div>
    `;
    }).join('');
  }

  t(key, fallback) {
    if (!this.i18n || typeof this.i18n.t !== 'function') {
      return fallback;
    }
    const translation = this.i18n.t(key);
    return translation === key ? fallback : translation;
  }

  isFeatured(item) {
    if (!item) return false;
    const badge = (item.badge || '').toString().toLowerCase();
    if (badge === 'featured' || badge === 'recommended') {
      return true;
    }
    const status = (item.status || '').toString().toLowerCase();
    if (status === 'recommended') {
      return true;
    }
    if (item.isPromotedToday || item.metadata?.isPromotedToday) {
      return true;
    }
    return false;
  }

  renderBadge(item) {
    if (!item) return '';
    const badgeKey = (item.badge || '').toString().toLowerCase();
    const label = item.badgeText || this.t('menu.badge.recommended', 'Recommended');

    if (this.isFeatured(item)) {
      return `
        <div class="badge-corner" aria-label="${label}">
          <span class="badge-star" aria-hidden="true">★</span>
          <span class="badge-label">${label}</span>
        </div>
      `;
    }

    if (badgeKey) {
      return `<span class="badge ${item.badge}">${item.badgeText || item.badge}</span>`;
    }
    return '';
  }

  rebindCategoryEvents(categoryContent) {
    const menuItemCards = categoryContent.querySelectorAll('.menu-item-card');
    menuItemCards.forEach(card => {
      card.addEventListener('click', (e) => {
        if (e.target.closest('.add-to-cart') || e.target.closest('.image-nav-btn')) {
          return;
        }

        const itemId = card.getAttribute('data-item');
        const item = this.findItemById(itemId);
        if (item) {
          const categoryItems = this.menuData.items[this.currentCategory] || [];
          this.menuItemModal.show(item, categoryItems);
        }
      });
    });

    const addToCartBtns = categoryContent.querySelectorAll('.add-to-cart');
    addToCartBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const itemId = btn.getAttribute('data-item');
        this.handleAddToCart(itemId, btn);
      });
    });

    const imageNavBtns = categoryContent.querySelectorAll('.image-nav-btn');
    imageNavBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const direction = btn.getAttribute('data-direction');
        const card = btn.closest('.menu-item-card');
        this.switchImage(card, direction);
      });
    });
  }

  handleAddToCart(itemId, buttonElement) {
    const item = this.findItemById(itemId);
    if (!item) {
      console.error('Item not found:', itemId);
      return;
    }

    const cartItem = {
      id: item.id,
      name: item.name,
      price: item.price?.amount || item.price || 0,
      quantity: 1,
      image: item.image || item.imageUrl,
      currency: item.currency || this.config.settings?.currency || this.config.currency || 'PLN',
      modifiers: {},
      specialInstructions: '',
      totalPrice: item.price?.amount || item.price || 0
    };

    const event = new CustomEvent('itemAddedToCart', {
      detail: cartItem
    });
    document.dispatchEvent(event);

    this.animateAddToCartButton(buttonElement);
  }

  /**
   * Animate add-to-cart button with satisfying feedback
   */
  animateAddToCartButton(buttonElement) {
    buttonElement.innerHTML = '<i class="fas fa-check"></i>';
    buttonElement.style.background = 'var(--color-success, #3E885B)';
    buttonElement.style.color = 'white';
    buttonElement.style.transform = 'scale(1.2)';
    buttonElement.style.transition = 'all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)';

    setTimeout(() => {
      buttonElement.style.transform = 'scale(1.1)';
    }, 150);

    setTimeout(() => {
      buttonElement.innerHTML = '<i class="fas fa-plus"></i>';
      buttonElement.style.background = '';
      buttonElement.style.color = '';
      buttonElement.style.transform = '';
      buttonElement.style.transition = 'all 0.3s ease';
    }, 800);
  }

  bindImageNavigation() {
    const imageNavBtns = document.querySelectorAll('.hamburger-layout .image-nav-btn');
    imageNavBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const direction = btn.getAttribute('data-direction');
        const card = btn.closest('.menu-item-card');
        this.switchImage(card, direction);
      });
    });
  }

  switchImage(card, direction) {
    const container = card.querySelector('.item-image-container');
    const img = container.querySelector('.item-image');
    const indicator = card.querySelector('.image-indicator');

    if (!img || !img.dataset.images) return;

    const images = JSON.parse(img.dataset.images);
    const currentIndex = parseInt(container.dataset.currentImage || '0');

    let newIndex;
    if (direction === 'next') {
      newIndex = (currentIndex + 1) % images.length;
    } else {
      newIndex = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
    }

    img.src = images[newIndex];
    container.dataset.currentImage = newIndex;

    if (indicator) {
      indicator.textContent = `${newIndex + 1} of ${images.length}`;
    }
  }

  findItemById(itemId) {
    for (const categoryItems of Object.values(this.menuData.items)) {
      const item = categoryItems.find(item => item.id === itemId);
      if (item) return item;
    }
    return null;
  }

  show() {
    this.showContainer();

    setTimeout(() => {
      this.showContainer();
    }, 100);

    setTimeout(() => {
      this.showContainer();
      this.loadInitialCategoryWithAnimations();
    }, 500);
  }

  showContainer() {
    const container = document.querySelector('.main-container.hamburger-layout');
    if (container) {
      container.style.display = 'block';
      container.style.visibility = 'visible';
      container.style.opacity = '0';
      container.style.position = 'relative';
      container.style.zIndex = '1';

      requestAnimationFrame(() => {
        container.style.transition = 'opacity 0.4s ease';
        container.style.opacity = '1';
      });
    }
  }

  loadInitialCategoryWithAnimations() {
    const activeContent = document.querySelector('.hamburger-layout .category-content.active');
    if (activeContent) {
      const menuGrid = activeContent.querySelector('.menu-items-grid');
      if (menuGrid) {
        this.animationManager.animateMenuItems(menuGrid, 50);
      }
    }
  }

  hide() {
    return new Promise((resolve) => {
      const container = document.querySelector('.main-container.hamburger-layout');
      if (container) {
        container.style.display = 'none';
        this.closeMenu();
      }
      resolve();
    });
  }

  updateCartCount(count) {
    this.cartCount = count;
    const cartCountEl = document.querySelector('.hamburger-layout .cart-count');
    if (cartCountEl) {
      cartCountEl.textContent = count;
    }
  }

  getSupportedFeatures() {
    return ['search', 'cart', 'hamburgerMenu', 'categories'];
  }
}
